import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { LoaderCircle, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import * as Query from '../../../../api/gql/query'
import { useDebounce } from '../../../../hooks/useDebounce'
import { classifySearchQuery } from '../../lib/search-query'
import type { ScheduleExternalActivity } from '../../types'

const MIN_QUERY_LEN = 2
const DEBOUNCE_MS = 250
const TAKE = 20

interface Props {
  projectId: string
  scheduleId: string
  scheduleName: string
  excludeActivityId: string
  rowNumberById: Map<string, number>
  /** Resolves a row number of THIS schedule to a picker result; null when unknown. */
  localByRowNumber: (n: number) => ScheduleExternalActivity | null
  disabled?: boolean
  onSelect: (activity: ScheduleExternalActivity) => void
  /** Bare row number + Enter: the caller adds the link immediately (type/lag from its own controls). */
  onSubmit: (activity: ScheduleExternalActivity) => void
}

interface Group { name: string; items: ScheduleExternalActivity[] }

/**
 * Cross-schedule activity search for `DependenciesSection`'s "Add predecessor/successor" row
 * (Task 17, spec B §9.7 step 5). Deliberately does NOT pass `excludeScheduleId` to
 * `SCHEDULE_ACTIVITY_SEARCH` — this schedule's own activities must appear too (grouped first), so a
 * local link can be added the same way as a cross-schedule one.
 *
 * Freelo #32253582: a bare row number (`1`, `12`) resolves instantly and locally instead of round
 * tripping to the server — `classifySearchQuery` on the *undebounced* input decides the branch.
 */
const ActivitySearchCombobox: React.FC<Props> = ({
  projectId, scheduleId, scheduleName, excludeActivityId, rowNumberById, localByRowNumber, disabled, onSelect, onSubmit,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query, DEBOUNCE_MS)
  const [highlighted, setHighlighted] = useState(0)

  const parsed = useMemo(() => classifySearchQuery(query), [query])
  const isRow = parsed.kind === 'row'
  const debouncedParsed = useMemo(() => classifySearchQuery(debouncedQuery), [debouncedQuery])
  const trimmed = debouncedParsed.kind === 'text' ? debouncedParsed.q : ''

  const { data, loading } = useQuery(Query.SCHEDULE_ACTIVITY_SEARCH, {
    variables: { projectId, query: trimmed, take: TAKE },
    skip: isRow || trimmed.length < MIN_QUERY_LEN,
    // Fix round 1, review finding #10: a session-long `cache-first` default could surface a stale
    // name for a result right after renaming the very activity that matches the search string.
    fetchPolicy: 'cache-and-network',
  })

  const rowMatch = useMemo(() => {
    if (parsed.kind !== 'row') return null
    const activity = localByRowNumber(parsed.n)
    if (!activity) return { activity: null, isSelf: false }
    return { activity, isSelf: activity.id === excludeActivityId }
  }, [parsed, localByRowNumber, excludeActivityId])

  const groups = useMemo<Group[]>(() => {
    if (parsed.kind === 'row') {
      if (rowMatch?.activity && !rowMatch.isSelf) return [{ name: scheduleName, items: [rowMatch.activity] }]
      return []
    }
    const results: ScheduleExternalActivity[] = (data?.scheduleActivitySearch ?? []).filter((a: ScheduleExternalActivity) => a.id !== excludeActivityId)
    const own = results.filter((a) => a.scheduleId === scheduleId)
    const foreign = results.filter((a) => a.scheduleId !== scheduleId)
    const order: string[] = []
    const byGroup = new Map<string, ScheduleExternalActivity[]>()
    if (own.length) { order.push(scheduleName); byGroup.set(scheduleName, own) }
    foreign.forEach((a) => {
      const list = byGroup.get(a.scheduleName)
      if (list) { list.push(a); return }
      order.push(a.scheduleName)
      byGroup.set(a.scheduleName, [a])
    })
    return order.map((name) => ({ name, items: byGroup.get(name) ?? [] }))
  }, [parsed, rowMatch, data, excludeActivityId, scheduleId, scheduleName])

  const flatItems = useMemo(() => groups.reduce<ScheduleExternalActivity[]>((acc, g) => acc.concat(g.items), []), [groups])
  const flatItemsKey = flatItems.map((a) => a.id).join('|')

  useEffect(() => { setHighlighted(0) }, [flatItemsKey])

  const itemLabel = (a: ScheduleExternalActivity) => (a.scheduleId === scheduleId ? `${rowNumberById.get(a.id) ?? a.wbsCode} ${a.name}` : `${a.wbsCode} ${a.name}`)

  const select = (a: ScheduleExternalActivity) => {
    onSelect(a)
    setQuery('')
    setOpen(false)
  }

  const submit = (a: ScheduleExternalActivity) => {
    onSubmit(a)
    setQuery('')
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (flatItems.length) setHighlighted((i) => (i + 1) % flatItems.length)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (flatItems.length) setHighlighted((i) => (i - 1 + flatItems.length) % flatItems.length)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (parsed.kind === 'row' && rowMatch?.activity && !rowMatch.isSelf) { submit(rowMatch.activity); return }
      const item = flatItems[highlighted] ?? flatItems[0]
      if (item) select(item)
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  const rowError = parsed.kind === 'row' && !groups.length
    ? (rowMatch?.isSelf ? t('Schedule_pred_error_self', { token: String(parsed.n) }) : t('Schedule_pred_error_unknown_row', { token: String(parsed.n) }))
    : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="h-8 pl-7 text-sm"
            placeholder={t('Schedule_search_placeholder')}
            value={query}
            disabled={disabled}
            onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="max-h-64 overflow-y-auto py-1">
          {loading && <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground mx-auto my-2" />}
          {!loading && rowError && <p className="px-3 py-2 text-xs text-muted-foreground">{rowError}</p>}
          {!loading && !rowError && parsed.kind === 'text' && groups.length === 0 && trimmed.length >= MIN_QUERY_LEN && (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t('Schedule_search_no_results')}</p>
          )}
          {!loading && groups.map((g) => (
            <div key={g.name}>
              <div className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground truncate">{g.name}</div>
              {g.items.map((a) => {
                const isHighlighted = flatItems[highlighted]?.id === a.id
                return (
                  <button
                    key={a.id}
                    type="button"
                    className={cn('w-full text-left px-3 py-1.5 text-sm hover:bg-accent truncate', isHighlighted && 'bg-accent')}
                    onClick={() => select(a)}
                  >
                    {itemLabel(a)}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default ActivitySearchCombobox
